import { Comment } from 'antd';
import { Dictionary } from 'lodash';
import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Reaction,
  ReactionProps,
  UserMessage as IUserMessage,
} from 'api/conversations';
import { User } from 'api/models';
import { PrivateStyles, withPrivacy } from 'components/atoms/private';
import { UserMessageReply } from 'components/molecules/user-message-reply';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useExternalIntegrationUserDetails } from 'hooks/use-external-integration-user-details';
import { useHolidayCalendar } from 'hooks/use-holiday-calendars';
import { useGetAllNativeUsersMap } from 'hooks/use-idb';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { fullName } from 'utils/full-name';
import { getThumbnailUrl } from 'utils/get-thumbnail-url';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { checkIfCurrentDateHasHoliday } from 'utils/holiday-calendar-utils';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';
import {
  UserMessageCommentAuthor as CommentAuthor,
  UserMessageCommentAvatar as CommentAvatar,
} from '../components';
import { AuthorTime } from './author-time';
import { UserMessageCommentReactions } from './comment-reactions';
import { UserMessageRichText } from './comment-rich-text';
import { FilesList } from './files-list';
import { KebabMenu } from './kebab-menu';
import { MessageOrigin } from './message-origin';
import { useIsFromSameUser } from './use-is-from-same-user';

const UserMessageWrapper = withPrivacy(
  styled.div`
    position: relative;

    .user-message__date {
      opacity: 0;
    }

    &:hover {
      --rte-background: var(--coolest-gray-10);

      background: var(--coolest-gray-10);

      .user-message__date {
        opacity: 1;
      }
    }

    .ant-comment-inner {
      padding: var(--spacing-03) var(--spacing-05) var(--spacing-03)
        var(--spacing-02);
    }

    .kebab__menu {
      left: auto !important;
      right: 16px;
      top: -16px !important;
    }
  `,
  PrivateStyles,
);

const CommentMetaWrapper = styled.div`
  display: flex;
  align-items: center;
`;

type CommentUpdateProps = {
  commentId: number;
  content: string;
};

type UserMessageProps = {
  className?: string;
  comment: IUserMessage;
  prevComment?: IUserMessage;
  onAddReactionToComment(commentId: number, reactionType: Reaction): void;
  onCommentUpdate?({ content, commentId }: CommentUpdateProps): void;
  onPerformReply({ commentId }): void;
  onPerformDelete(commentId: number): void;
  disallowMentions?: boolean;
  userLookup: Dictionary<User>;
};

export const UserMessage: FC<UserMessageProps> = ({
  className,
  comment,
  prevComment,
  onAddReactionToComment,
  onCommentUpdate,
  onPerformReply,
  onPerformDelete,
  disallowMentions = false,
  userLookup,
}) => {
  const [canEditComment, setCanEditComment] = useState(false);
  const [reactions, setReactions] = useState<ReactionProps[]>([]);
  const [commentContainerRef, setCommentContainerRef] =
    useState<HTMLDivElement | null>(null);

  const [reactionTriggerState, toggleReactionTriggerState] = useState(false);
  const [showKebabMenu, toggleKebabMenuState] = useState(false);
  const isFromSameUser = useIsFromSameUser(comment, prevComment);

  const confirmPopupTitleText = useTranslation('delete_confirmation_message');
  const confirmPopupContentText = useTranslation(
    'delete_cannot_be_undone_message',
  );
  const confirmPopupOkText = useTranslation('yes');
  const confirmPopupCancelText = useTranslation('no');
  const repliedUser = useExternalIntegrationUserDetails(
    comment.commentMeta?.externalUser,
    comment.commentMeta?.repliedTo?.user as User,
  );

  const showCommentDeletionConfirmation = useDeleteConfirmModal({
    title: confirmPopupTitleText,
    content: confirmPopupContentText,
    okText: confirmPopupOkText,
    cancelText: confirmPopupCancelText,
    onOk: () => onPerformDelete(comment.commentId),
  });

  useEffect(() => {
    setReactions(comment.multiReactionSummary?.summaries || []);
  }, [comment.multiReactionSummary]);

  useEffect(() => {
    toggleKebabMenuState(!(reactionTriggerState || canEditComment));
  }, [reactionTriggerState, canEditComment]);

  const commentContainer = useCallback((node) => {
    if (node !== null) {
      setCommentContainerRef(node);
    }
  }, []);

  const onCanEditChange = (editable: boolean) => {
    setCanEditComment(editable);
  };

  const saveComment = (content: string) => {
    if (!content) {
      showCommentDeletionConfirmation();

      return true;
    }
    setCanEditComment(false);
    onCommentUpdate?.({
      commentId: comment.commentId,
      content,
    });

    return true;
  };

  const cancelChanges = () => {
    setCanEditComment(false);
  };

  const { user: currentUser } = useUser();

  const { holidayCalendar } = useHolidayCalendar(
    userLookup[comment?.user?.userId]?.holidayCalendarId,
  );
  const holidays = holidayCalendar?.holidays;

  const userTimeOff = checkIfCurrentDateHasTimeOff(
    userLookup[comment?.user?.userId]?.userTimeOffDetails,
  );

  const userHoliday = checkIfCurrentDateHasHoliday(holidays);

  /**
   * TODO:: (HACK) Clean this up.
   * @param reactionType heart | thumbUp | clap
   * 1. Find if the reactionType is existing in the array, if yes proceed to 3 else 2
   * 2. While the reaction is not present, create a grouping to be pushed into `reactions`
   * 3. Find if the users array of the reactionType exists or not. if it does proceed to 4, else 5
   * 4. If it exists, that user needs to be removed from the array
   * 5. Otherwise, add that to the users array.
   *
   * [
   *  {
   *    reactionType: 'heart',
   *    users: []
   *  }
   * ]
   *
   * [
   *  {}
   * ]
   *
   * [
   *  {
   *    reactionType: 'heart',
   *    users: [{
   *      userId: !CurrentUser
   *    }]
   *  }
   * ]
   */
  const onChangeReaction = (reactionType: Reaction): void => {
    toggleReactionTriggerState(false);
    onAddReactionToComment(comment.commentId, reactionType);

    let constructedReactions = [...reactions];
    let reactionToUpdate = constructedReactions.find(
      (reaction) => reaction.reactionType === reactionType,
    );

    if (!reactionToUpdate) {
      reactionToUpdate = {
        reactionType,
        users: [],
      };
      constructedReactions = [...constructedReactions, reactionToUpdate];
    }

    const index = reactionToUpdate.users.findIndex(
      (u) => u.userId === currentUser.userId,
    );

    if (index > -1) {
      reactionToUpdate.users = [
        ...reactionToUpdate.users.slice(0, index),
        ...reactionToUpdate.users.slice(index + 1),
      ];

      if (!reactionToUpdate.users.length) {
        constructedReactions = constructedReactions.filter(
          (reaction) => reaction.reactionType !== reactionType,
        );
      }
    } else {
      reactionToUpdate.users = [...reactionToUpdate.users, currentUser];
    }

    setReactions(constructedReactions);
  };
  /* --- Clean up --- */

  const userDetails = useExternalIntegrationUserDetails(
    comment.commentMeta?.externalUser,
    comment.user,
  );
  const userFullName = fullName(userDetails);

  // @TODO This will get all the users as map still the memory usage is high
  // Need to find a way to get the userstatus data insidet the userDetails
  const usersMap = useGetAllNativeUsersMap();

  return (
    <UserMessageWrapper
      data-comment={tryGetTextFromHTML(comment?.content ?? '<p></p>')}
      className={className}
      private={comment.private}
      ref={commentContainer}
    >
      <KebabMenu
        attachTo={commentContainerRef}
        comment={comment}
        readOnly={!comment.commentId}
        visible={showKebabMenu}
        onAddReaction={onChangeReaction}
        onPerformEdit={onCanEditChange}
        onPerformDelete={() => onPerformDelete(comment.commentId)}
        onMarkReply={() => onPerformReply({ commentId: comment.commentId })}
        showConfirm={showCommentDeletionConfirmation}
      >
        <Comment
          author={
            !canEditComment &&
            isFromSameUser && (
              <CommentAuthor
                userFullName={userFullName}
                timeOff={userTimeOff}
                holiday={userHoliday}
              />
            )
          }
          avatar={
            <CommentAvatar
              userStatus={usersMap?.[userDetails?.userId]?.userStatus}
              createdAt={comment.createdAt}
              isFromSameUser={isFromSameUser}
              userFullName={userFullName}
              avatarUrl={getThumbnailUrl(userDetails.photoUrl)}
            />
          }
          content={
            <>
              {comment.commentMeta?.repliedTo && (
                <UserMessageReply
                  attachments={comment.commentMeta.repliedTo.linkedAttachments}
                  isPrivate={comment.commentMeta.repliedTo.private}
                  content={comment.commentMeta.repliedTo.content || ''}
                  replyAuthor={repliedUser}
                />
              )}

              <UserMessageRichText
                disallowMentions={disallowMentions}
                canEditComment={canEditComment}
                content={comment.content}
                isPrivate={comment.private}
                cancelChanges={cancelChanges}
                saveComment={saveComment}
              />
              {!canEditComment && (
                <>
                  {comment.commentMeta?.linkedAttachments && (
                    <FilesList files={comment.commentMeta?.linkedAttachments} />
                  )}
                  {reactions.length > 0 && (
                    <UserMessageCommentReactions
                      currentUser={currentUser}
                      parentRef={commentContainerRef}
                      reactions={reactions}
                      onChangeReaction={onChangeReaction}
                      onReactionVisibilityChange={toggleReactionTriggerState}
                    />
                  )}
                </>
              )}
            </>
          }
          datetime={
            !canEditComment &&
            isFromSameUser && (
              <CommentMetaWrapper>
                <AuthorTime user={userDetails} createdAt={comment.createdAt} />
                {comment.commentMeta?.externalUser ? (
                  <MessageOrigin
                    type={comment.commentMeta.externalUser.integrationType}
                  />
                ) : null}
              </CommentMetaWrapper>
            )
          }
        />
      </KebabMenu>
    </UserMessageWrapper>
  );
};
